"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _core = require("@linaria/core");
var _breakpoints = require("@site/pattern-library-utils/breakpoints");
var _density = require("@site/pattern-library-utils/density");
var _section = _interopRequireDefault(require("@site/pattern-library-core/section"));
var _useElementProps = _interopRequireDefault(require("@site/pattern-library-utils/hooks/useElementProps"));
var _motionFunctions = require("@site/pattern-library-core/motion-functions");
var _base = require("@site/pattern-library-utils/base.functions");
var _configContext = require("./config-context");
var _rotationControl = _interopRequireDefault(require("./rotation-control"));
var _helpers = require("./helpers");
var _marqueeFunctions = require("../../page-header/marquees/motion-campaign-marquee/marquee-functions");
var _mediaContent = _interopRequireDefault(require("./media-content"));
var _boxModelSize = require("@site/pattern-library-utils/box-model-size");
var _intents = _interopRequireDefault(require("@site/pattern-library-utils/intents"));
var _headlineLockup = _interopRequireDefault(require("../../elements/other/headline-lockup"));
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const headlineContainer = "pl-h1bwpwhw";
const innerContent = "pl-ivbuea0";
const container = "pl-c13uzpn7";
const centerLayout = "pl-c10exkc2";
const headlineContainerCenter = "pl-h9w42ke";
const containerWithInsetTop = "pl-c1fwhlbb";
const headlineContainerWithoutInsetTop = "pl-h1uaism";
const section = "pl-sa533hu";
const ProcessedHeadline = ({
  headlineLockup,
  layoutProp
}) => {
  const propsOverrides = (0, _react.useMemo)(() => (0, _helpers.getHeadlinePropsOverrides)(headlineLockup, layoutProp), [headlineLockup, layoutProp]);
  const processedElement = (0, _useElementProps.default)(headlineLockup, propsOverrides);
  if (/*#__PURE__*/(0, _react.isValidElement)(headlineLockup)) {
    return processedElement;
  }
  if (headlineLockup && typeof headlineLockup === 'object') {
    return /*#__PURE__*/(0, _react.createElement)(_headlineLockup.default, {
      ...headlineLockup,
      ...propsOverrides
    });
  }
  return null;
};
ProcessedHeadline.propTypes = {
  headlineLockup: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.object]),
  layoutProp: _propTypes.default.string
};
const checkHasVideo = videoProps => {
  return Boolean(videoProps?.introUrl || videoProps?.introUrlWebm || videoProps?.introUrlLaptop || videoProps?.introUrlLaptopWebm || videoProps?.videos && /*#__PURE__*/(0, _react.isValidElement)(videoProps.videos));
};

/**
 * RotatingMarquee Component.
 *
 * @param {Object} props React props
 * @param {string} props.id Section ID
 * @param {string} props._name Section Name
 * @param {string} props.layout Layout style ('default' or 'center')
 * @param {Array} props.groups Array of groups; each group contains a headlineLockup, marqueeName, and media
 * @param {boolean} props.autoRotate Enable auto-rotation
 * @param {number} props.autoRotateInterval Auto-rotation interval in milliseconds (default: 10000ms)
 * @param {string} props.navElements Navigation elements string (e.g., 'tld-searchbar|secondary-nav')
 * @returns {React.ReactElement} RotatingMarquee Component
 * @public
 */
function RotatingMarquee({
  id,
  _name,
  layout = 'default',
  groups,
  autoRotate = false,
  autoRotateInterval = 10000,
  insetTop = false,
  partial = '',
  navElements = ''
}) {
  const {
    width
  } = (0, _base.useWindowSize)();
  const isMobile = width > 0 && width < _breakpoints.size.tablet;
  const intervalValue = autoRotate ? autoRotateInterval : 0;
  const groupItems = (0, _react.useMemo)(() => (0, _helpers.normalizeToArray)(groups), [groups]);
  const [activeIndex, setActiveIndex] = (0, _react.useState)(0);
  const [leavingIndex, setLeavingIndex] = (0, _react.useState)(null);
  const hasAnyVideo = groupItems.some(group => (0, _helpers.hasVideoContent)(group));
  const [isAutoRotating, setIsAutoRotating] = (0, _react.useState)(!isMobile && (autoRotate && groupItems.length > 1 || hasAnyVideo));
  const [isVideoPaused, setIsVideoPaused] = (0, _react.useState)(false);
  const {
    mobileHeight,
    desktopHeight
  } = (0, _marqueeFunctions.calculateNavHeights)(navElements);
  const handleRotate = (0, _react.useCallback)(() => {
    setLeavingIndex(activeIndex);
    setTimeout(() => setLeavingIndex(null), 500);
    setActiveIndex(prev => (prev + 1) % groupItems.length);
  }, [activeIndex, groupItems.length]);
  const {
    start: startAutoRotation,
    stop: stopAutoRotation
  } = (0, _helpers.useAutoRotation)({
    interval: intervalValue,
    itemCount: groupItems.length,
    onRotate: handleRotate,
    enabled: isAutoRotating
  });
  (0, _react.useEffect)(() => {
    // Disable auto-rotation on mobile regardless of autoRotate prop
    const shouldRotate = !isMobile && (autoRotate && groupItems.length > 1 || hasAnyVideo);
    setIsAutoRotating(shouldRotate);
  }, [autoRotate, groupItems.length, hasAnyVideo, isMobile]);
  const handleItemSelect = (0, _react.useCallback)((newIndex, shouldStopAutoRotate = true) => {
    if (newIndex === activeIndex || newIndex < 0 || newIndex >= groupItems.length) {
      return;
    }
    if (shouldStopAutoRotate) {
      setIsAutoRotating(false);
      stopAutoRotation();
    }
    setLeavingIndex(activeIndex);
    setTimeout(() => setLeavingIndex(null), 500);
    setActiveIndex(newIndex);
    setIsVideoPaused(false);
  }, [activeIndex, groupItems.length, stopAutoRotation]);
  const handleUserInteract = (0, _react.useCallback)(() => {
    setIsAutoRotating(false);
    stopAutoRotation();
  }, [stopAutoRotation]);
  const handleCarouselNavigate = (0, _react.useCallback)(newIndex => {
    if (newIndex === activeIndex || newIndex < 0 || newIndex >= groupItems.length) return;
    setLeavingIndex(activeIndex);
    setTimeout(() => setLeavingIndex(null), 500);
    setActiveIndex(newIndex);
    setIsVideoPaused(false);
    const shouldStartRotation = !isAutoRotating && !isMobile;
    if (shouldStartRotation) {
      setIsAutoRotating(true);
      startAutoRotation();
    }
  }, [activeIndex, groupItems.length, isAutoRotating, startAutoRotation, isMobile]);
  const handleToggleAutoRotate = (0, _react.useCallback)(() => {
    if (isMobile) return;
    setIsAutoRotating(!isAutoRotating);
    isAutoRotating ? stopAutoRotation() : startAutoRotation();
  }, [isAutoRotating, startAutoRotation, stopAutoRotation, isMobile]);
  const handleToggleVideo = (0, _react.useCallback)(() => {
    setIsVideoPaused(prev => !prev);
  }, []);
  const hasMultipleItems = groupItems.length > 1;
  const sectionStyle = {
    '--nav-mobile-height': `${mobileHeight}px`,
    '--nav-desktop-height': `${desktopHeight}px`
  };
  return /*#__PURE__*/_react.default.createElement(_configContext.ConfigContext.Provider, {
    value: {
      layout
    }
  }, /*#__PURE__*/_react.default.createElement(_section.default, {
    id: `id-${id}`,
    _name: _name,
    dataCy: "rotating-marquee",
    verticalPadding: "none",
    verticalMargin: "none",
    className: section,
    style: sectionStyle
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: (0, _core.cx)(container, layout === 'center' && centerLayout, insetTop && containerWithInsetTop)
  }, /*#__PURE__*/_react.default.createElement("div", {
    className: "rotating-marquee-content"
  }, groupItems.map((group, index) => /*#__PURE__*/_react.default.createElement("div", {
    key: index,
    className: (0, _core.cx)(innerContent, index === activeIndex && 'active', index === leavingIndex && 'leaving'),
    id: `marquee-${id}-content-${index}`,
    role: "tabpanel",
    "aria-hidden": index !== activeIndex
  }, (() => {
    const videoProps = (0, _helpers.collectVideoProps)(group);
    const hasVideo = checkHasVideo(videoProps);
    const videoPausedState = isMobile ? isVideoPaused : !isAutoRotating;
    return /*#__PURE__*/_react.default.createElement("div", {
      className: (0, _core.cx)('media-container', hasVideo && 'has-video')
    }, /*#__PURE__*/_react.default.createElement(_mediaContent.default, {
      portrait: group?.portrait,
      landscape: group?.landscape,
      tablet: group?.tablet,
      videoProps: videoProps,
      paused: videoPausedState
    }));
  })(), /*#__PURE__*/_react.default.createElement("div", {
    className: (0, _core.cx)(headlineContainer, layout === 'center' && headlineContainerCenter, !insetTop && headlineContainerWithoutInsetTop),
    "data-partial": (0, _base.getDataPartial)(partial, (0, _helpers.getHeadlineLockupInvert)(group?.headlineLockup))
  }, /*#__PURE__*/_react.default.createElement(ProcessedHeadline, {
    headlineLockup: group?.headlineLockup,
    layoutProp: layout
  }))))), hasMultipleItems && /*#__PURE__*/_react.default.createElement(_rotationControl.default, {
    items: groupItems,
    activeIndex: activeIndex,
    onItemSelect: i => handleItemSelect(i, false),
    onUserInteract: handleUserInteract,
    onCarouselNavigate: handleCarouselNavigate,
    isAutoRotating: isAutoRotating,
    autoRotateInterval: intervalValue,
    autoRotate: autoRotate,
    ariaLabel: "marquee navigation controls",
    baseId: `marquee-${id}`,
    onToggleAutoRotate: handleToggleAutoRotate,
    onToggleVideo: handleToggleVideo,
    isVideoPaused: isVideoPaused,
    isMobile: isMobile
  }))));
}
var _default = exports.default = RotatingMarquee;
RotatingMarquee.propTypes = {
  id: _propTypes.default.string,
  _name: _propTypes.default.string,
  layout: _propTypes.default.oneOf(['default', 'center']),
  groups: _propTypes.default.arrayOf(_propTypes.default.shape({
    headlineLockup: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.object]),
    marqueeName: _propTypes.default.string,
    portrait: _propTypes.default.shape(_boxModelSize.bynderImageProps),
    landscape: _propTypes.default.shape(_boxModelSize.bynderImageProps),
    tablet: _propTypes.default.shape(_boxModelSize.bynderImageProps),
    thumbnailDesktop: _propTypes.default.string,
    thumbnailMobile: _propTypes.default.string,
    introUrl: _propTypes.default.string,
    introUrlWebm: _propTypes.default.string,
    introUrlLaptop: _propTypes.default.string,
    introUrlLaptopWebm: _propTypes.default.string,
    videos: _propTypes.default.node,
    preload: _propTypes.default.string
  })),
  autoRotate: _propTypes.default.bool,
  autoRotateInterval: _propTypes.default.number,
  rotationControlsAriaLabel: _propTypes.default.string,
  insetTop: _propTypes.default.bool,
  partial: _propTypes.default.string,
  navElements: _propTypes.default.string
};